import React, { useState, useEffect, useRef } from 'react';
import { 
  ThermometerSun, Droplets, Wind, Edit, Save, X, 
  TrendingUp, Activity, BarChart3, Wifi, WifiOff,
  Database, Clock, AlertCircle, CheckCircle 
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const CircularGauge = ({ value, maxValue, title, unit, optimal, lastUpdate }) => {
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const percentage = (value / maxValue) * 100;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  const isOptimal = optimal.min <= value && value <= optimal.max;
  
  return (
    <div className="flex flex-col items-center p-6 sm:p-8 bg-gradient-to-br from-gray-800 to-gray-900 backdrop-blur-sm rounded-2xl shadow-2xl transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] border border-gray-700 w-full relative">
      {/* Last update indicator */}
      {lastUpdate && (
        <div className="absolute top-3 right-3">
          <div className="text-xs text-gray-400 bg-gray-900/80 px-2 py-1 rounded-full">
            {lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      )}
      
      <div className="relative w-44 h-44 sm:w-52 sm:h-52">
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            strokeWidth="12"
            className="fill-none stroke-gray-700"
          />
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            strokeWidth="12"
            strokeLinecap="round"
            className={`fill-none transition-all duration-700 ease-out ${
              isOptimal ? 'stroke-emerald-500' : 'stroke-rose-500'
            }`}
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: strokeDashoffset
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl sm:text-5xl font-bold text-white">{value.toFixed(1)}</span>
          <span className="text-sm sm:text-base text-gray-300 font-medium mt-1">{unit}</span>
        </div>
      </div>
      <h3 className="mt-4 sm:mt-6 text-xl sm:text-2xl font-bold text-white flex items-center gap-3">
        {title === 'Temperature' && <ThermometerSun size={24} className="text-blue-400" />}
        {title === 'Humidity' && <Droplets size={24} className="text-green-400" />}
        {title === 'CO2 Level' && <Wind size={24} className="text-purple-400" />}
        {title}
      </h3>
      <div className={`mt-3 sm:mt-4 text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-medium ${isOptimal ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'}`}>
        {isOptimal ? 'Optimal' : 'Warning'}
      </div>
    </div>
  );
};

const OptimalRangeEditor = ({ type, currentRange, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [minValue, setMinValue] = useState(currentRange.min);
  const [maxValue, setMaxValue] = useState(currentRange.max);

  const handleSave = () => {
    onSave({ min: parseFloat(minValue), max: parseFloat(maxValue) });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setMinValue(currentRange.min);
    setMaxValue(currentRange.max);
    setIsEditing(false);
  };

  const getIcon = () => {
    switch(type) {
      case 'temperature': return <ThermometerSun className="text-blue-400" size={24} />;
      case 'humidity': return <Droplets className="text-green-400" size={24} />;
      case 'co2': return <Wind className="text-purple-400" size={24} />;
      default: return null;
    }
  };

  const getTitle = () => {
    switch(type) {
      case 'temperature': return 'Temperature';
      case 'humidity': return 'Humidity';
      case 'co2': return 'CO2 Level';
      default: return '';
    }
  };

  const getUnit = () => {
    switch(type) {
      case 'temperature': return '°C';
      case 'humidity': return '%';
      case 'co2': return 'ppm';
      default: return '';
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gradient-to-br from-gray-800 to-gray-900 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-700 relative">
      <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
        {getIcon()}
        <h3 className="text-lg sm:text-xl font-bold text-white">Optimal {getTitle()}</h3>
        {!isEditing ? (
          <button 
            onClick={() => setIsEditing(true)}
            className="ml-auto p-1 text-gray-400 hover:text-white transition-colors"
            aria-label="Edit"
          >
            <Edit size={18} />
          </button>
        ) : (
          <div className="ml-auto flex space-x-2">
            <button 
              onClick={handleSave}
              className="p-1 text-emerald-400 hover:text-emerald-300 transition-colors"
              aria-label="Save"
            >
              <Save size={18} />
            </button>
            <button 
              onClick={handleCancel}
              className="p-1 text-rose-400 hover:text-rose-300 transition-colors"
              aria-label="Cancel"
            >
              <X size={18} />
            </button>
          </div>
        )}
      </div>
      
      {isEditing ? (
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <label className="text-gray-300 font-medium w-16">Min:</label>
            <input
              type="number"
              value={minValue}
              onChange={(e) => setMinValue(e.target.value)}
              className="flex-1 p-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center space-x-3">
            <label className="text-gray-300 font-medium w-16">Max:</label>
            <input
              type="number"
              value={maxValue}
              onChange={(e) => setMaxValue(e.target.value)}
              className="flex-1 p-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      ) : (
        <>
          <p className="text-gray-300 font-medium">
            {currentRange.min}-{currentRange.max}{getUnit()}
          </p>
          <p className="text-xs sm:text-sm text-gray-400 mt-1 sm:mt-2">
            {type === 'temperature' && 'Maintain comfortable temperature for productivity'}
            {type === 'humidity' && 'Prevent dry air or excessive moisture'}
            {type === 'co2' && 'Ensure good air quality and ventilation'}
          </p>
        </>
      )}
    </div>
  );
};

const EnvironmentMonitoring = () => {
  const [data, setData] = useState({
    temperature: 0,
    humidity: 0,
    carbonDioxide: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isReceivingData, setIsReceivingData] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [dataStats, setDataStats] = useState({
    receivedCount: 0,
    storageInterval: '1 minute',
    bufferSize: 0
  });
  
  const [optimalRanges, setOptimalRanges] = useState({
    temperature: { min: 20, max: 25 },
    humidity: { min: 30, max: 60 },
    carbonDioxide: { min: 0, max: 1000 }
  });

  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const dataCountRef = useRef(0);
  const chartBufferRef = useRef([]);

  const handleRangeChange = (type, newRange) => {
    setOptimalRanges(prev => ({
      ...prev,
      [type]: newRange
    }));
  };

  const connectWebSocket = () => {
    if (socketRef.current) {
      socketRef.current.close();
    }

    const wsUrl = process.env.REACT_APP_ENDPOINT_WS || 'ws://localhost:5000';
    
    try {
      socketRef.current = new WebSocket(wsUrl);

      socketRef.current.onopen = () => {
        console.log('Environment WebSocket connected');
        setIsConnected(true);
        setConnectionStatus('connected');
        dataCountRef.current = 0;
        setDataStats(prev => ({ ...prev, receivedCount: 0 }));
      };

      socketRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Handle connection message
          if (message.type === 'connection') {
            console.log(`Environment: Storage interval = ${message.interval}`);
            setDataStats(prev => ({ 
              ...prev, 
              storageInterval: message.interval || '1 minute' 
            }));
            return;
          }
          
          // Handle aggregated data notification
          if (message.type === 'aggregated' && message.dataType === 'environment') {
            console.log('Environment aggregated data stored:', message.data);
            return;
          }
          
          // Handle real-time data
          let envData = {};
          
          if (message.type === 'realtime') {
            envData = message;
          } 
          // Handle legacy format
          else if (!message.type && message.temperature !== undefined) {
            envData = message;
          }
          
          if (envData.temperature !== undefined && 
              envData.humidity !== undefined && 
              envData.carbonDioxide !== undefined) {
            
            const now = new Date();
            setLastUpdate(now);
            dataCountRef.current++;
            setIsReceivingData(true);
            
            // Update real-time display
            setData({
              temperature: envData.temperature,
              humidity: envData.humidity,
              carbonDioxide: envData.carbonDioxide
            });
            
            // Update chart data (store only one point per 10 seconds for performance)
            const shouldAddToChart = chartBufferRef.current.length === 0 || 
              (now - new Date(chartBufferRef.current[chartBufferRef.current.length - 1].timestamp)) > 10000;
            
            if (shouldAddToChart) {
              const newEntry = {
                timestamp: now,
                hour: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`,
                temperature: parseFloat(envData.temperature.toFixed(1)),
                humidity: parseFloat(envData.humidity.toFixed(1)),
                carbonDioxide: parseInt(envData.carbonDioxide.toFixed(0))
              };
              
              chartBufferRef.current.push(newEntry);
              
              // Keep only last 4 hours of data (approximately 1440 points at 10-second intervals)
              const fourHoursAgo = new Date(now.getTime() - 4 * 60 * 60 * 1000);
              chartBufferRef.current = chartBufferRef.current.filter(
                item => new Date(item.timestamp) >= fourHoursAgo
              );
              
              setChartData([...chartBufferRef.current]);
            }
            
            // Update stats periodically
            if (dataCountRef.current % 10 === 0) {
              setDataStats(prev => ({ 
                ...prev, 
                receivedCount: dataCountRef.current,
                bufferSize: chartBufferRef.current.length
              }));
            }
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      socketRef.current.onclose = (event) => {
        console.log(`Environment WebSocket disconnected: ${event.code} ${event.reason}`);
        setIsConnected(false);
        setIsReceivingData(false);
        setConnectionStatus('disconnected');
        
        // Reset data when disconnected
        setData({
          temperature: 0,
          humidity: 0,
          carbonDioxide: 0
        });
        
        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          if (socketRef.current?.readyState !== WebSocket.OPEN) {
            connectWebSocket();
          }
        }, 5000);
      };

      socketRef.current.onerror = (error) => {
        console.error('Environment WebSocket error:', error);
        setIsConnected(false);
        setConnectionStatus('error');
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      setConnectionStatus('error');
    }
  };

  useEffect(() => {
    connectWebSocket();
    
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // Monitor data reception
  useEffect(() => {
    const checkDataTimeout = setInterval(() => {
      if (lastUpdate) {
        const timeSinceLastUpdate = Date.now() - lastUpdate.getTime();
        if (timeSinceLastUpdate > 10000 && isReceivingData) {
          setIsReceivingData(false);
        }
      }
    }, 1000);

    return () => clearInterval(checkDataTimeout);
  }, [lastUpdate, isReceivingData]);

  const getConnectionStatus = () => {
    if (isReceivingData) return { text: 'Receiving Data', color: 'text-emerald-400', icon: <Wifi className="w-5 h-5" /> };
    if (isConnected) return { text: 'Connected', color: 'text-blue-400', icon: <Wifi className="w-5 h-5" /> };
    if (connectionStatus === 'error') return { text: 'Connection Error', color: 'text-rose-400', icon: <AlertCircle className="w-5 h-5" /> };
    return { text: 'Disconnected', color: 'text-gray-400', icon: <WifiOff className="w-5 h-5" /> };
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 backdrop-blur-md p-5 rounded-2xl shadow-2xl border border-gray-700 transform transition-all duration-200">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="text-blue-400" size={16} />
            <p className="font-bold text-white text-sm">{`Time: ${label}`}</p>
          </div>
          <div className="space-y-2">
            {payload.map((entry) => (
              <div key={entry.dataKey} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full shadow-sm" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-xs font-medium text-gray-300">
                    {entry.dataKey === 'temperature' && 'Temperature'}
                    {entry.dataKey === 'humidity' && 'Humidity'}
                    {entry.dataKey === 'carbonDioxide' && 'CO2'}
                  </span>
                </div>
                <span className="text-sm font-bold text-white">
                  {entry.dataKey === 'temperature' && `${entry.value}°C`}
                  {entry.dataKey === 'humidity' && `${entry.value}%`}
                  {entry.dataKey === 'carbonDioxide' && `${entry.value} ppm`}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const connectionStatusInfo = getConnectionStatus();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header Section */}
        <div className="text-center mb-8 sm:mb-12 px-2">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 sm:mb-6">
            Environment Monitoring
          </h1>
          
          {/* Connection Status Bar */}
          <div className="inline-flex items-center justify-center gap-6 bg-gradient-to-r from-gray-800/50 to-gray-800/30 backdrop-blur-sm px-6 sm:px-8 py-4 rounded-2xl border border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${isReceivingData ? 'bg-emerald-400 animate-pulse' : 'bg-gray-400'}`}></div>
              <div className="flex items-center gap-2">
                {connectionStatusInfo.icon}
                <span className={`font-medium ${connectionStatusInfo.color}`}>
                  {connectionStatusInfo.text}
                </span>
              </div>
            </div>
            
            <div className="h-6 w-px bg-gray-600"></div>
            
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-400" />
              <span className="text-gray-300">
                {lastUpdate ? lastUpdate.toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  second: '2-digit' 
                }) : 'Never updated'}
              </span>
            </div>
            
            <div className="h-6 w-px bg-gray-600"></div>
            
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-400" />
              <span className="text-gray-300">
                Storage: <span className="font-bold text-blue-300">{dataStats.storageInterval}</span>
              </span>
            </div>
          </div>
          
          {/* Data Efficiency Info */}
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-gray-400">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span>60x more efficient data storage</span>
            <span className="bg-emerald-400/20 text-emerald-300 px-2 py-1 rounded-full text-xs">
              {dataStats.receivedCount} data points
            </span>
          </div>
        </div>

        {/* Real-time Gauges */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-10 mb-12 sm:mb-16">
          <CircularGauge
            value={data.temperature}
            maxValue={40}
            title="Temperature"
            unit="°C"
            optimal={optimalRanges.temperature}
            lastUpdate={lastUpdate}
          />
          <CircularGauge
            value={data.humidity}
            maxValue={100}
            title="Humidity"
            unit="%"
            optimal={optimalRanges.humidity}
            lastUpdate={lastUpdate}
          />
          <CircularGauge
            value={data.carbonDioxide}
            maxValue={2000}
            title="CO2 Level"
            unit="ppm"
            optimal={optimalRanges.carbonDioxide}
            lastUpdate={lastUpdate}
          />
        </div>

        {/* Optimal Range Settings */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-12 sm:mb-16">
          <OptimalRangeEditor 
            type="temperature"
            currentRange={optimalRanges.temperature}
            onSave={(newRange) => handleRangeChange('temperature', newRange)}
          />
          <OptimalRangeEditor 
            type="humidity"
            currentRange={optimalRanges.humidity}
            onSave={(newRange) => handleRangeChange('humidity', newRange)}
          />
          <OptimalRangeEditor 
            type="co2"
            currentRange={optimalRanges.carbonDioxide}
            onSave={(newRange) => handleRangeChange('carbonDioxide', newRange)}
          />
        </div>

        {/* Data Storage Information */}
        <div className="mb-12 p-6 bg-gradient-to-r from-gray-800/50 to-gray-800/30 rounded-2xl border border-gray-700">
          <h3 className="text-xl font-bold text-white mb-4">Data Storage System</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-gray-800/50 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Database className="text-blue-400" size={20} />
                </div>
                <div>
                  <div className="text-lg font-bold text-white">1-Minute Aggregation</div>
                  <div className="text-sm text-gray-400">Data stored every 60 seconds</div>
                </div>
              </div>
              <p className="text-gray-300 text-sm">
                Real-time data is buffered and stored as aggregated values (average, min, max) every minute.
              </p>
            </div>
            
            <div className="p-4 bg-gray-800/50 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <CheckCircle className="text-emerald-400" size={20} />
                </div>
                <div>
                  <div className="text-lg font-bold text-white">60x Efficiency</div>
                  <div className="text-sm text-gray-400">Reduced database writes</div>
                </div>
              </div>
              <p className="text-gray-300 text-sm">
                From 86,400 records/day to 1,440 records/day while maintaining data integrity.
              </p>
            </div>
            
            <div className="p-4 bg-gray-800/50 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Activity className="text-purple-400" size={20} />
                </div>
                <div>
                  <div className="text-lg font-bold text-white">Real-time Display</div>
                  <div className="text-sm text-gray-400">Continuous updates</div>
                </div>
              </div>
              <p className="text-gray-300 text-sm">
                Frontend still receives data every second for smooth, real-time visualization.
              </p>
            </div>
          </div>
        </div>

        {/* Historical Trends Chart */}
        <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 backdrop-blur-md rounded-3xl shadow-2xl border border-gray-700 p-6 sm:p-8 overflow-hidden">
          {/* Header */}
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between mb-8 sm:mb-10 gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
                <TrendingUp className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white">
                  4-Hour Environmental Trends
                </h2>
                <p className="text-sm text-gray-400">Real-time monitoring with efficient data storage</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 rounded-xl border border-gray-600">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span className="text-sm text-gray-300">Temperature</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 rounded-xl border border-gray-600">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-sm text-gray-300">Humidity</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 rounded-xl border border-gray-600">
                <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                <span className="text-sm text-gray-300">CO2 Level</span>
              </div>
            </div>
          </div>
          
          {/* Current Stats */}
          <div className="relative z-10 grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-800/30 p-4 rounded-xl border border-gray-700">
              <div className="flex items-center gap-2 mb-2">
                <ThermometerSun className="text-blue-400" size={16} />
                <span className="text-xs font-semibold text-blue-300">TEMPERATURE</span>
              </div>
              <div className="text-xl font-bold text-white">{data.temperature.toFixed(1)}°C</div>
              <div className="text-xs text-gray-400 mt-1">
                Range: {optimalRanges.temperature.min}°C - {optimalRanges.temperature.max}°C
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-800/30 p-4 rounded-xl border border-gray-700">
              <div className="flex items-center gap-2 mb-2">
                <Droplets className="text-green-400" size={16} />
                <span className="text-xs font-semibold text-green-300">HUMIDITY</span>
              </div>
              <div className="text-xl font-bold text-white">{data.humidity.toFixed(1)}%</div>
              <div className="text-xs text-gray-400 mt-1">
                Range: {optimalRanges.humidity.min}% - {optimalRanges.humidity.max}%
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-800/30 p-4 rounded-xl border border-gray-700">
              <div className="flex items-center gap-2 mb-2">
                <Wind className="text-purple-400" size={16} />
                <span className="text-xs font-semibold text-purple-300">CO2 LEVEL</span>
              </div>
              <div className="text-xl font-bold text-white">{data.carbonDioxide} ppm</div>
              <div className="text-xs text-gray-400 mt-1">
                Range: {optimalRanges.carbonDioxide.min} - {optimalRanges.carbonDioxide.max} ppm
              </div>
            </div>
          </div>
          
          {/* Chart Area */}
          {chartData.length === 0 ? (
            <div className="relative z-10 h-96 sm:h-[500px] flex items-center justify-center">
              <div className="text-center">
                <div className="relative">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-700 border-t-blue-500 mx-auto mb-6"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <BarChart3 className="text-blue-400" size={20} />
                  </div>
                </div>
                <p className="text-gray-300 font-medium">Loading chart data...</p>
                <p className="text-sm text-gray-500 mt-1">
                  {isReceivingData ? 'Receiving environmental data...' : 'Waiting for data connection...'}
                </p>
                <p className="text-xs text-gray-600 mt-2">
                  Data points in buffer: {dataStats.bufferSize}
                </p>
              </div>
            </div>
          ) : (
            <div className="relative z-10 h-96 sm:h-[500px] bg-gradient-to-br from-gray-900/50 to-gray-800/30 rounded-2xl p-4 border border-gray-700">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                  <defs>
                    <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="humidityGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="co2Gradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke="#374151" 
                    strokeOpacity={0.6}
                    vertical={false}
                  />
                  <XAxis 
                    dataKey="hour" 
                    stroke="#9CA3AF"
                    fontSize={11}
                    fontWeight={500}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tick={{ fill: '#9CA3AF' }}
                  />
                  <YAxis 
                    stroke="#9CA3AF" 
                    fontSize={11}
                    fontWeight={500}
                    tick={{ fill: '#9CA3AF' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    wrapperStyle={{ 
                      paddingTop: '30px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#fff'
                    }}
                    iconType="circle"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="temperature" 
                    stroke="#3b82f6" 
                    strokeWidth={3}
                    dot={{ 
                      fill: '#1F2937', 
                      stroke: '#3b82f6',
                      strokeWidth: 2, 
                      r: 3,
                      filter: 'drop-shadow(0px 2px 4px rgba(59, 130, 246, 0.3))'
                    }}
                    activeDot={{ 
                      r: 6, 
                      strokeWidth: 2,
                      fill: '#3b82f6',
                      stroke: '#1F2937',
                      filter: 'drop-shadow(0px 4px 8px rgba(59, 130, 246, 0.4))'
                    }}
                    name="Temperature (°C)"
                    fill="url(#tempGradient)"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="humidity" 
                    stroke="#10b981" 
                    strokeWidth={3}
                    dot={{ 
                      fill: '#1F2937', 
                      stroke: '#10b981',
                      strokeWidth: 2, 
                      r: 3,
                      filter: 'drop-shadow(0px 2px 4px rgba(16, 185, 129, 0.3))'
                    }}
                    activeDot={{ 
                      r: 6, 
                      strokeWidth: 2,
                      fill: '#10b981',
                      stroke: '#1F2937',
                      filter: 'drop-shadow(0px 4px 8px rgba(16, 185, 129, 0.4))'
                    }}
                    name="Humidity (%)"
                    fill="url(#humidityGradient)"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="carbonDioxide" 
                    stroke="#8b5cf6" 
                    strokeWidth={3}
                    dot={{ 
                      fill: '#1F2937', 
                      stroke: '#8b5cf6',
                      strokeWidth: 2, 
                      r: 3,
                      filter: 'drop-shadow(0px 2px 4px rgba(139, 92, 246, 0.3))'
                    }}
                    activeDot={{ 
                      r: 6, 
                      strokeWidth: 2,
                      fill: '#8b5cf6',
                      stroke: '#1F2937',
                      filter: 'drop-shadow(0px 4px 8px rgba(139, 92, 246, 0.4))'
                    }}
                    name="CO2 (ppm)"
                    fill="url(#co2Gradient)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          
          {/* Chart Footer */}
          <div className="relative z-10 mt-6 flex flex-col sm:flex-row sm:items-center justify-between text-sm text-gray-400">
            <div>
              <span className="text-gray-300">Chart shows last 4 hours of data</span>
              <span className="mx-2">•</span>
              <span>{chartData.length} data points</span>
              <span className="mx-2">•</span>
              <span>Updated every 10 seconds</span>
            </div>
            <div className="mt-2 sm:mt-0">
              <span className="text-emerald-400">Storage: {dataStats.storageInterval} intervals</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentMonitoring;